import { useState } from "react";
import EcoleShell from "@/components/layout/EcoleShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Search, Pencil, AlertTriangle } from "lucide-react";
import { useParents, useUpdateParent } from "@/lib/backoffice-hooks";
import type { ParentFiche } from "@/lib/api";
import { friendlyError } from "@/lib/errors";

/**
 * Fiches parents de l'école — consultation et CORRECTION (2026-08-14).
 *
 * Pourquoi cet écran existe : le nom porté par la fiche est celui du PAYEUR sur
 * les reçus. Une fiche créée automatiquement à la première visite d'un parent
 * prenait le morceau gauche de son email (« sergeassale »), et personne ne
 * pouvait la corriger — ni le parent, ni l'école. C'est la porte de sortie.
 *
 * L'établissement n'est jamais transmis : le serveur ramène un compte d'école à
 * SON école, et refuse d'en servir une autre.
 */
export default function EcoleParentsPage() {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const { data: parents, isLoading } = useParents(query);
  const [edit, setEdit] = useState<ParentFiche | null>(null);

  const rows = parents ?? [];

  return (
    <EcoleShell>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-navy">Fiches parents</h1>
        <p className="text-sm text-slate-600">
          Le nom enregistré ici apparaît sur les reçus de paiement.
        </p>
      </div>

      <form
        className="mb-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(search);
        }}
      >
        <Input
          data-testid="parents-search"
          placeholder="Nom, prénoms, téléphone ou email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
        />
        <Button type="submit" data-testid="parents-search-submit">
          <Search className="h-4 w-4 mr-2" />
          Rechercher
        </Button>
      </form>

      <Card className="border border-slate-200 shadow-none overflow-hidden">
        {isLoading ? (
          <div className="p-8 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Chargement des fiches…
          </div>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500" data-testid="parents-empty">
            Aucune fiche parent pour cette recherche.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Nom et prénoms</th>
                <th className="px-4 py-3">Téléphone</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`parent-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-navy">{p.nom}</span>{" "}
                    <span className="text-slate-600">{p.prenoms}</span>
                    {ficheIncomplete(p) ? (
                      <span
                        className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700"
                        data-testid={`parent-incomplete-${p.id}`}
                      >
                        <AlertTriangle className="h-3 w-3" />À compléter
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{p.telephone || "·"}</td>
                  <td className="px-4 py-3 text-slate-600">{p.email || "·"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`parent-edit-${p.id}`}
                      onClick={() => setEdit(p)}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Corriger
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {edit ? <EditFicheDialog fiche={edit} onClose={() => setEdit(null)} /> : null}
    </EcoleShell>
  );
}

/**
 * Signale les fiches vraisemblablement créées automatiquement : prénoms
 * manquants ou nom identique au début de l'email. C'est le stock à reprendre.
 */
export function ficheIncomplete(p: ParentFiche): boolean {
  if (!p.prenoms || !p.prenoms.trim()) return true;
  const [local] = (p.email ?? "").split("@");
  return local !== "" && local.toLowerCase() === p.nom.trim().toLowerCase();
}

/** Tous les champs en chaîne : le formulaire ne manipule jamais `undefined`,
 *  la conversion en champs facultatifs se fait à l'envoi. */
type FormState = Record<"nom" | "prenoms" | "telephone" | "email" | "profession" | "adresse", string>;

function EditFicheDialog({ fiche, onClose }: { fiche: ParentFiche; onClose: () => void }) {
  const update = useUpdateParent();
  const [form, setForm] = useState<FormState>({
    nom: fiche.nom,
    prenoms: fiche.prenoms,
    telephone: fiche.telephone,
    email: fiche.email ?? "",
    profession: fiche.profession ?? "",
    adresse: fiche.adresse ?? "",
  });

  function set(field: keyof FormState, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.nom.trim()) {
      toast.error("Le nom est obligatoire.");
      return;
    }
    if (!form.telephone.trim()) {
      toast.error("Le téléphone est obligatoire.");
      return;
    }
    update.mutate(
      {
        id: fiche.id,
        data: {
          nom: form.nom.trim(),
          prenoms: form.prenoms.trim(),
          telephone: form.telephone.trim(),
          email: form.email.trim() || undefined,
          profession: form.profession.trim() || undefined,
          adresse: form.adresse.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success("Fiche corrigée.");
          onClose();
        },
        onError: (err: unknown) => toast.error(friendlyError(err)),
      },
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-lg p-6" data-testid="parent-edit-dialog">
        <h2 className="text-lg font-bold text-navy">Corriger la fiche</h2>
        <p className="mt-1 text-xs text-slate-500">
          Ces informations servent à joindre la famille et nomment le payeur sur les reçus.
          Elles ne changent ni l&apos;identifiant de connexion du parent, ni son accès à
          l&apos;application.
        </p>

        <form className="mt-4 space-y-3" onSubmit={submit}>
          <div className="grid grid-cols-2 gap-3">
            <Field id="nom" label="Nom" value={form.nom} onChange={(v) => set("nom", v)} required />
            <Field
              id="prenoms"
              label="Prénoms"
              value={form.prenoms}
              onChange={(v) => set("prenoms", v)}
            />
          </div>
          <Field
            id="telephone"
            label="Téléphone"
            value={form.telephone}
            onChange={(v) => set("telephone", v)}
            required
          />
          <Field id="email" label="Email" value={form.email} onChange={(v) => set("email", v)} />
          <Field
            id="profession"
            label="Profession"
            value={form.profession}
            onChange={(v) => set("profession", v)}
          />
          <Field
            id="adresse"
            label="Adresse"
            value={form.adresse}
            onChange={(v) => set("adresse", v)}
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="parent-edit-cancel">
              Annuler
            </Button>
            <Button type="submit" disabled={update.isPending} data-testid="parent-edit-submit">
              {update.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Enregistrer
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  required,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <div>
      <Label htmlFor={`parent-${id}`}>
        {label}
        {required ? " *" : ""}
      </Label>
      <Input
        id={`parent-${id}`}
        data-testid={`parent-field-${id}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
